import {
	CALL_OUTCOME_MEANING_LABELS,
	CALL_OUTCOME_MEANINGS,
	REPLY_CATEGORY_MEANING_LABELS,
	REPLY_CATEGORY_MEANINGS,
} from '../constants/campaign-outcome-meanings';
import { Capabilities, type CapabilityAlias } from '../constants/capabilities';
import { LEAD_SOURCE_MEANING_LABELS, LEAD_SOURCE_MEANINGS } from '../constants/lead-sources';
import {
	COMPETITOR_ENGAGEMENT_STATUS_OUTCOME_LABELS,
	COMPETITOR_ENGAGEMENT_STATUS_OUTCOMES,
} from '../constants/competitor-engagement-statuses';
import { FILE_RECORD_TYPES, type FileRecordType } from '../constants/file-types';
import { JOB_CUSTOMER_STATUS_OUTCOME_LABELS, JOB_CUSTOMER_STATUS_OUTCOMES } from '../constants/job-customer-roles';
import { CatalogType } from '../enums/catalog-type.enum';

/** One outcome a catalog value may map to, with its label for the settings screen. */
export interface CatalogOutcomeOptionModel {
	value: string;
	label: string;
}

export interface CatalogTypeConfig {
	/** Discriminator value stored on `catalog_value.catalogType`. */
	type: CatalogType;
	/** Singular human label (e.g. for a "New X" button). */
	label: string;
	/** Plural human label (e.g. for a section header). */
	pluralLabel: string;
	/** One-line explanation shown in the management screen. */
	description: string;
	/**
	 * When set, this catalog is the CHILD level of a two-level hierarchy and
	 * each of its values' `parentId` points at a value of `parentType`
	 * (e.g. a model belongs to a manufacturer, a subcategory to a category).
	 * The management screen nests children under their parent and the
	 * consuming editor cascades the child dropdown off the chosen parent.
	 */
	parentType?: CatalogType;
	/**
	 * PD-17109 — when set, every value of this catalog maps to one of these
	 * fixed outcomes (`catalog_value.outcome`), the way an opportunity stage
	 * maps to open / won / lost: admins rename the values freely while code,
	 * reports and the Sales Helper read the outcome. The service requires a
	 * listed outcome on every value; catalogs without the list never carry one.
	 */
	outcomes?: ReadonlyArray<CatalogOutcomeOptionModel>;
	/** The outcome a value gets when it is added without one. */
	defaultOutcome?: string;
	/**
	 * PD-17109 — the add-on the catalog belongs to: the settings screen shows
	 * it only where the tenant holds that capability.
	 */
	requiredCapability?: CapabilityAlias;
	/**
	 * PD-17326: a file-type list, for this record type. Settings shows these
	 * together under File Types, one list per record type.
	 */
	fileRecordType?: FileRecordType;
}

function outcomeOptions(values: ReadonlyArray<string>, labels: Readonly<Record<string, string>>): Array<CatalogOutcomeOptionModel> {
	return values.map((value) => ({ value, label: labels[value] ?? value }));
}

/**
 * The single source of truth for every consolidated managed catalog
 * (PD-14226, Option A). Adding a future catalog is one entry here plus a
 * CatalogType enum member — the generic table, CRUD API, and management
 * screen pick it up automatically; no new table/migration/endpoint.
 */
export const CATALOG_TYPE_REGISTRY: Record<CatalogType, CatalogTypeConfig> = {
	[CatalogType.MerchandiserManufacturer]: {
		type: CatalogType.MerchandiserManufacturer,
		label: 'Merchandiser Manufacturer',
		pluralLabel: 'Merchandiser Manufacturers',
		description: 'Manufacturers of merchandiser (cooler) units.',
	},
	[CatalogType.MerchandiserModel]: {
		type: CatalogType.MerchandiserModel,
		label: 'Merchandiser Model',
		pluralLabel: 'Merchandiser Models',
		description:
			'Models of merchandiser units. Each model belongs to a manufacturer.',
		parentType: CatalogType.MerchandiserManufacturer,
	},
	[CatalogType.ClientBusinessType]: {
		type: CatalogType.ClientBusinessType,
		label: 'Business Type',
		pluralLabel: 'Business Types',
		description:
			'Suggested business types for the client form. These are non-binding suggestions — users can still enter any value, and the field is not restricted to this list.',
	},
	[CatalogType.VehicleMake]: {
		type: CatalogType.VehicleMake,
		label: 'Vehicle Make',
		pluralLabel: 'Vehicle Makes',
		description: 'Makes (manufacturers) of fleet vehicles.',
	},
	[CatalogType.VehicleModel]: {
		type: CatalogType.VehicleModel,
		label: 'Vehicle Model',
		pluralLabel: 'Vehicle Models',
		description: 'Models of fleet vehicles. Each model belongs to a make.',
		parentType: CatalogType.VehicleMake,
	},
	[CatalogType.SoftphoneCallDisposition]: {
		type: CatalogType.SoftphoneCallDisposition,
		label: 'Call Disposition',
		pluralLabel: 'Call Dispositions',
		description:
			'Outcomes a rep can pick when wrapping up a softphone call (e.g. "Spoke to customer", "Left voicemail"). Each outcome means one thing to campaigns and account stages, whatever it is called. Reps can still enter a custom outcome via "Other…".',
		// PD-17606 — the meaning campaigns read (conversation, attempt, ...).
		outcomes: outcomeOptions(CALL_OUTCOME_MEANINGS, CALL_OUTCOME_MEANING_LABELS),
		defaultOutcome: 'attempt',
	},
	// PD-16048 — the Dispatch label lists. Order matters: the picker shows
	// values in sortOrder, so the ones drivers use most belong at the top.
	[CatalogType.DispatchAdjustmentReason]: {
		type: CatalogType.DispatchAdjustmentReason,
		label: 'Adjustment Reason',
		pluralLabel: 'Adjustment Reasons',
		description:
			'Reasons offered in the mid-route Adjust Inventory page. Drivers and warehouse managers can still type a custom reason via "Custom…".',
	},
	[CatalogType.DispatchStopOutcomeReason]: {
		type: CatalogType.DispatchStopOutcomeReason,
		label: 'Skip-Stop Reason',
		pluralLabel: 'Skip-Stop Reasons',
		description:
			'Reasons offered to the driver when a stop cannot be completed normally — customer refusal, store closed, blocked entrance. A value named "Other" reveals a free-text field in the driver picker.',
	},
	[CatalogType.DispatchPauseCategory]: {
		type: CatalogType.DispatchPauseCategory,
		label: 'Pause Category',
		pluralLabel: 'Pause Categories',
		description:
			'Reasons offered to the driver when pausing a trip — meal, fuel, mechanical. Pauses are recorded as stops on the trip timeline.',
	},
	// PD-16801 — the Log Visit lists.
	[CatalogType.ClientVisitType]: {
		type: CatalogType.ClientVisitType,
		label: 'Visit Type',
		pluralLabel: 'Visit Types',
		description:
			'How a rep made a customer visit (e.g. "On-site", "Phone"). Offered in the Log Visit dialog and filterable on the Visit Log report.',
	},
	[CatalogType.ClientVisitOutcome]: {
		type: CatalogType.ClientVisitOutcome,
		label: 'Visit Outcome',
		pluralLabel: 'Visit Outcomes',
		description:
			'What came of a customer visit (e.g. "Order placed", "Quote requested"). Offered in the Log Visit dialog and filterable on the Visit Log report.',
	},
	// PD-17101 — required when an opportunity is lost.
	[CatalogType.OpportunityLostReason]: {
		type: CatalogType.OpportunityLostReason,
		label: 'Lost Reason',
		pluralLabel: 'Lost Reasons',
		description:
			'Why an opportunity was lost (e.g. "Price", "Lead time or availability"). A rep must pick one when an opportunity moves to a Lost stage; the Win/Loss report groups by it.',
	},
	// PD-17794 — optional; a company with no values never sees the field.
	[CatalogType.OpportunityType]: {
		type: CatalogType.OpportunityType,
		label: 'Opportunity Type',
		pluralLabel: 'Opportunity Types',
		description:
			'How serious a pursuit is (e.g. "Budget", "Bid", "Bid to Buy"). Offered on the opportunity; it stays the same while the stage moves.',
	},
	// PD-17109 — the job competitor status list (jobs add-on).
	[CatalogType.CompetitorEngagementStatus]: {
		type: CatalogType.CompetitorEngagementStatus,
		label: 'Competitor Status',
		pluralLabel: 'Competitor Statuses',
		description:
			'Where a competitor on a job stands (e.g. "Quoting", "Supplying"). Each status means one outcome, which reports and the Sales Helper read whatever the status is called.',
		outcomes: outcomeOptions(COMPETITOR_ENGAGEMENT_STATUS_OUTCOMES, COMPETITOR_ENGAGEMENT_STATUS_OUTCOME_LABELS),
		defaultOutcome: 'quoting',
		requiredCapability: Capabilities.JOBS,
	},
	// PD-17604 — where a person or company came from (picked, never typed).
	[CatalogType.LeadSource]: {
		type: CatalogType.LeadSource,
		label: 'Lead Source',
		pluralLabel: 'Lead Sources',
		description:
			'Where a person or company came from (e.g. "Trade Show", "Referral"). Each source means one thing to reports, whatever it is called. Trade shows, referrals and email campaigns also name the show, who referred them or the campaign.',
		outcomes: outcomeOptions(LEAD_SOURCE_MEANINGS, LEAD_SOURCE_MEANING_LABELS),
		defaultOutcome: 'other',
	},
	// PD-17606 — Workd's own reply categories for campaign email (campaigns add-on).
	[CatalogType.ReplyCategory]: {
		type: CatalogType.ReplyCategory,
		label: 'Reply Category',
		pluralLabel: 'Reply Categories',
		description:
			'What a reply to a campaign email is (e.g. "Interested", "Not Now"). Each category means one thing to campaigns, whatever it is called.',
		outcomes: outcomeOptions(REPLY_CATEGORY_MEANINGS, REPLY_CATEGORY_MEANING_LABELS),
		defaultOutcome: 'needs_review',
		requiredCapability: Capabilities.CAMPAIGNS,
	},
	// PD-17448 — the role and status of each customer on a job (jobs add-on).
	[CatalogType.JobCustomerRole]: {
		type: CatalogType.JobCustomerRole,
		label: 'Job Customer Role',
		pluralLabel: 'Job Customer Roles',
		description:
			'The part a customer plays on a job (e.g. "Contractor", "General contractor", "Owner"). Each customer on a job has one.',
		requiredCapability: Capabilities.JOBS,
	},
	[CatalogType.JobCustomerStatus]: {
		type: CatalogType.JobCustomerStatus,
		label: 'Job Customer Status',
		pluralLabel: 'Job Customer Statuses',
		description:
			'Where a customer on a job stands (e.g. "Bidding", "Awarded"). Each status means one outcome, which the job page reads whatever the status is called.',
		outcomes: outcomeOptions(JOB_CUSTOMER_STATUS_OUTCOMES, JOB_CUSTOMER_STATUS_OUTCOME_LABELS),
		defaultOutcome: 'bidding',
		requiredCapability: Capabilities.JOBS,
	},
	// PD-17326 — one file-type list per record type (constants/file-types.ts).
	...(Object.fromEntries(
		FILE_RECORD_TYPES.map((config) => [
			config.catalogType,
			{
				type: config.catalogType,
				label: `${config.label} File Type`,
				pluralLabel: `${config.label} File Types`,
				description: `The types a file uploaded to a ${config.label.toLowerCase()} can be (e.g. "${config.defaults[0] ?? 'Document'}"). Turning a type off keeps the files that have it.`,
				fileRecordType: config.recordType,
				...(config.requiredCapability != null ? { requiredCapability: config.requiredCapability } : {}),
			} satisfies CatalogTypeConfig,
		]),
	) as Record<
		| CatalogType.ClientFileType
		| CatalogType.OpportunityFileType
		| CatalogType.JobFileType
		| CatalogType.IssueFileType
		| CatalogType.ItemFileType
		| CatalogType.LicenseFileType
		| CatalogType.SourcingRequestFileType
		| CatalogType.PurchaseOrderReceiptFileType
		| CatalogType.ContactFileType,
		CatalogTypeConfig
	>),
};

/**
 * PD-17109 — the outcome a value of `catalogType` is saved with: the one
 * given when it is on the catalog's list, the catalog's default when none is
 * given, and null for a catalog without outcomes. Throws on an outcome the
 * catalog does not list, so a typo never becomes a new outcome.
 */
export function resolveCatalogOutcome(catalogType: CatalogType, outcome: string | null | undefined): string | null {
	const config = CATALOG_TYPE_REGISTRY[catalogType];

	if (config.outcomes == null) {
		return null;
	}

	if (outcome == null || outcome === '') {
		return config.defaultOutcome ?? config.outcomes[0]?.value ?? null;
	}

	if (!config.outcomes.some((option) => option.value === outcome)) {
		throw new Error(
			`${config.label} outcome must be one of: ${config.outcomes.map((option) => option.label).join(', ')}`,
		);
	}

	return outcome;
}

/** All catalog types in registry (declaration) order. */
export const CATALOG_TYPES: ReadonlyArray<CatalogType> =
	Object.values(CatalogType);

/** Type guard for a string that may be a known catalog type. */
export function isCatalogType(value: string): value is CatalogType {
	return (CATALOG_TYPES as ReadonlyArray<string>).includes(value);
}
